import PropTypes from "prop-types";
import { useState, useCallback, useMemo } from "react";
import { Typography, Table, TableBody, TableRow, TableCell, Box, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { rainbowSurgePalette } from "@mui/x-charts/colorPalettes";
import { toPieData } from "./statsPieDataUtils.js";
import { applyPieSliceColors } from "./statsPieColors.js";

export const PIE_CHART_SIZE = { width: 220, height: 180 };

const PIE_SERIES_ID = "stats-pie";
const COLOR_DOT_SIZE = 10;

/**
 * Pie chart or empty placeholder when there is no data.
 * @param {Object} props
 * @param {Array<{ id: string, value: number, label: string, color?: string }>} [props.chartData] - pre-built slice data with colors
 * @param {Object<string, number>} [props.data] - used when chartData is omitted
 * @param {Object<string, string>} [props.labelMap]
 * @param {'value'|'label'|'state'} [props.sortBy]
 * @param {string[]} [props.stateOrder]
 * @param {import('@mui/x-charts').HighlightItemIdentifier | null} [props.highlightedItem]
 * @param {(item: import('@mui/x-charts').HighlightItemIdentifier | null) => void} [props.onHighlightChange]
 */
export function PieChartOrEmpty({
  chartData: chartDataProp,
  data,
  labelMap = {},
  sortBy = "value",
  stateOrder = [],
  highlightedItem = null,
  onHighlightChange,
}) {
  const theme = useTheme();
  const palette = rainbowSurgePalette(theme.palette.mode);

  const chartData = useMemo(() => {
    if (chartDataProp) {
      return chartDataProp;
    }
    return applyPieSliceColors(toPieData(data, { labelMap, sortBy, stateOrder }), palette);
  }, [chartDataProp, data, labelMap, sortBy, stateOrder, palette]);

  if (chartData.length === 0) {
    return (
      <Box
        sx={{
          ...PIE_CHART_SIZE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
        }}>
        <Typography variant="body2" color="text.secondary">
          Žádná data
        </Typography>
      </Box>
    );
  }
  return (
    <PieChart
      series={[
        {
          id: PIE_SERIES_ID,
          data: chartData,
          arcLabel: "value",
          arcLabelMinAngle: 10,
          arcLabelRadius: 65,
          highlightScope: { fade: "global", highlight: "item" },
          highlighted: { additionalRadius: 2 },
        },
      ]}
      colors={palette}
      hideLegend
      highlightedItem={highlightedItem}
      onHighlightChange={onHighlightChange}
      {...PIE_CHART_SIZE}
    />
  );
}

PieChartOrEmpty.propTypes = {
  chartData: PropTypes.array,
  data: PropTypes.object,
  labelMap: PropTypes.object,
  sortBy: PropTypes.string,
  stateOrder: PropTypes.array,
  highlightedItem: PropTypes.object,
  onHighlightChange: PropTypes.func,
};

/**
 * Pie chart with companion count table (color dot, label, count) synced with slice highlight.
 * @param {Object} props
 * @param {Object<string, number>} props.data
 * @param {Object<string, string>} [props.labelMap]
 * @param {'value'|'label'|'state'} [props.sortBy]
 * @param {string[]} [props.stateOrder]
 * @param {string} props.title
 */
export function ChartWithTable({ data, labelMap = {}, sortBy = "value", stateOrder = [], title }) {
  const theme = useTheme();
  const palette = rainbowSurgePalette(theme.palette.mode);
  const [highlightedItem, setHighlightedItem] = useState(null);

  const chartData = useMemo(
    () => applyPieSliceColors(toPieData(data, { labelMap, sortBy, stateOrder }), palette),
    [data, labelMap, sortBy, stateOrder, palette],
  );

  const handleHighlightChange = useCallback((item) => {
    setHighlightedItem(item);
  }, []);

  const highlightSlice = useCallback((dataIndex) => {
    setHighlightedItem({ type: "pie", seriesId: PIE_SERIES_ID, dataIndex });
  }, []);

  const clearHighlight = useCallback(() => {
    setHighlightedItem(null);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <PieChartOrEmpty
        chartData={chartData}
        highlightedItem={highlightedItem}
        onHighlightChange={handleHighlightChange}
      />
      {chartData.length > 0 && (
        <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 }, maxWidth: 320 }}>
          <TableBody>
            {chartData.map(({ id, value, label, color }, dataIndex) => {
              const isHighlighted = highlightedItem?.dataIndex === dataIndex;
              return (
                <TableRow
                  key={id}
                  hover
                  selected={isHighlighted}
                  onMouseEnter={() => highlightSlice(dataIndex)}
                  onMouseLeave={clearHighlight}
                  sx={{ cursor: "default" }}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: COLOR_DOT_SIZE,
                          height: COLOR_DOT_SIZE,
                          borderRadius: "50%",
                          bgcolor: color,
                          flexShrink: 0,
                        }}
                      />
                      {label}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{value}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

ChartWithTable.propTypes = {
  data: PropTypes.object.isRequired,
  labelMap: PropTypes.object,
  sortBy: PropTypes.string,
  stateOrder: PropTypes.array,
  title: PropTypes.string.isRequired,
};
